import React from 'react';
import { Box, Paper, Typography, IconButton, Button } from '@mui/material';
import { useDrop } from 'react-dnd';
import CloseIcon from '@mui/icons-material/Close';
import OperatorCard from './OperatorCard';

const ExpressionContainer = ({ expression, updateExpression, onSave, onClear }) => {
    console.log('ExpressionContainer', expression);
    const addOperatorAtIndex = (operator, index) => {
        updateExpression(prev => {
            const newExp = [...prev];
            newExp.splice(index, 0, operator);
            return newExp;
        });
    };

    const moveOperator = (fromIndex, toIndex) => {
        updateExpression(prev => {
            const newExp = [...prev];
            const [movedOperator] = newExp.splice(fromIndex, 1);
            // Adjust index if moving forward.
            const insertAt = fromIndex < toIndex ? toIndex - 1 : toIndex;
            newExp.splice(insertAt, 0, movedOperator);
            return newExp;
        });
    };

    const removeOperator = (index) => {
        updateExpression(prev => {
            const newExp = [...prev];
            newExp.splice(index, 1);
            return newExp;
        });
    };

    // New: update operator properties so that property changes are stored.
    const handleUpdateOperator = (index, newProperties) => {
        updateExpression(prev => {
            const newExp = [...prev];
            newExp[index] = { ...newExp[index], ...newProperties };
            return newExp;
        });
    };

    const DropSlot = ({ index }) => {
        const [{ isOver, draggedItem }, drop] = useDrop({
            accept: 'operator',
            drop: (item) => {
                if (item.fromExpression) {
                    moveOperator(item.originalIndex, index);
                } else {
                    addOperatorAtIndex(item.operator, index);
                }
            },
            collect: (monitor) => ({
                isOver: monitor.isOver(),
                draggedItem: monitor.getItem(),
            }),
        });

        return (
            <Box
                ref={drop}
                sx={{
                    flex: '0 0 20px',
                    height: 200,
                    width: 60,
                    backgroundColor: isOver ? 'primary.main' : draggedItem ? 'primary.light' : 'transparent',
                    borderRadius: 1,
                    transition: 'background-color 0.2s ease',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                {index !== 0 && (
                    <Typography variant="h6" color={draggedItem ? 'white' : 'black'}>
                        +
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Paper
            elevation={3}
            sx={{
                p: 2,
                minHeight: 200,
                textAlign: 'center',
                margin: '0 auto',
                maxWidth: 1200,
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Build Your Expression</Typography>
                <Box>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={onSave} 
                        sx={{ mr: 1 }}
                    >
                        SAVE EXPRESSION
                    </Button>
                    <Button 
                        variant="outlined" 
                        color="secondary" 
                        onClick={onClear}
                    >
                        CLEAR EXPRESSION
                    </Button>
                </Box>
            </Box>

            {/* Container for dropped operator cards */}
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    textAlign: 'left',
                    gap: 1,
                    margin: '0 auto',
                    minHeight: 150,
                }}
            >
                {/* Always have a DropSlot at the start */}
                <DropSlot index={0} />

                {expression.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                        Drag operators here...
                    </Typography>
                ) : (
                    expression.map((op, idx) => (
                        <React.Fragment key={idx}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    flex: '0 0 auto',
                                    boxSizing: 'border-box',
                                }}
                            >
                                <IconButton
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        color: 'red',
                                        zIndex: 2,
                                    }}
                                    onClick={() => removeOperator(idx)}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>

                                <OperatorCard
                                    operator={op}
                                    isExpressionItem
                                    expressionIndex={idx}
                                    onPropertyChange={handleUpdateOperator}
                                />
                            </Box>

                            {/* DropSlot inserted after each operator */}
                            <DropSlot index={idx + 1} />
                        </React.Fragment>
                    ))
                )}
            </Box>
        </Paper>
    );
};

export default ExpressionContainer;
